import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { millProductionAPI } from '../../services/millAPI';
import './Mill.css';

interface OrderItem {
  id: number;
  order_id: number;
  order_number: string;
  customer_name: string;
  formula_name: string;
  feed_form: string;
  total_quantity_lbs: number;
  number_of_packages: number;
  delivery_date: string;
  ship_date: string;  // delivery_date - 2 days
  status: string;
  status_display: string;
  is_rush: boolean;
}

interface CalendarData {
  start_date: string;
  end_date: string;
  orders_by_ship_date: { [date: string]: OrderItem[] };
  total_orders: number;
  total_weight: number;
}

const MillCalendar: React.FC = () => {
  const navigate = useNavigate();
  const [weekOffset, setWeekOffset] = useState(0);
  const [calendarData, setCalendarData] = useState<CalendarData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const getWeekDates = useCallback(() => {
    const today = new Date();
    const startOfWeek = new Date(today);
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Monday start
    startOfWeek.setDate(today.getDate() + diff + (weekOffset * 7));

    const dates: Date[] = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      dates.push(date);
    }
    return dates;
  }, [weekOffset]);

  const loadCalendarData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const weekDates = getWeekDates();
      const startDate = weekDates[0].toISOString().split('T')[0];
      const endDate = weekDates[6].toISOString().split('T')[0];

      // Fetch orders for the week
      const response = await millProductionAPI.getOrdersByShipDate({
        start_date: startDate,
        end_date: endDate
      });

      setCalendarData(response.data);
    } catch (err: any) {
      console.error('Error loading calendar data:', err);
      setError('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, [getWeekDates]);

  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateKey = (date: Date) => {
    return date.toISOString().split('T')[0];
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPast = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const getOrdersForDate = (date: Date): OrderItem[] => {
    if (!calendarData?.orders_by_ship_date) return [];
    const key = formatDateKey(date);
    return calendarData.orders_by_ship_date[key] || [];
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'PENDING':
      case 'CONFIRMED':
        return 'status-pending';
      case 'PRODUCTION':
      case 'IN_PRODUCTION':
        return 'status-production';
      case 'READY':
        return 'status-ready';
      case 'SHIPPED':
        return 'status-shipped';
      default:
        return '';
    }
  };

  const getDayTotal = (date: Date): { orders: number; weight: number } => {
    const orders = getOrdersForDate(date);
    return {
      orders: orders.length,
      weight: orders.reduce((sum, o) => sum + o.total_quantity_lbs, 0),
    };
  };

  const weekDates = getWeekDates();
  const weekStart = weekDates[0];
  const weekEnd = weekDates[6];

  const getWeekLabel = () => {
    if (weekOffset === 0) return 'This Week';
    if (weekOffset === 1) return 'Next Week';
    if (weekOffset === -1) return 'Last Week';
    return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  return (
    <div className="mill-page">
      {/* Header */}
      <div className="mill-dashboard-header">
        <div className="header-content">
          <h1>Production Calendar</h1>
          <p className="header-subtitle">Orders by Ship Date (Delivery - 2 Days)</p>
        </div>
        <div className="header-actions">
          <button
            className="mill-btn mill-btn-secondary"
            onClick={() => navigate('/mill/dashboard')}
          >
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Week Navigation */}
      <div style={styles.weekNav}>
        <button
          style={styles.navButton}
          onClick={() => setWeekOffset(prev => prev - 1)}
        >
          &larr; Previous
        </button>
        <div style={styles.weekLabel}>
          <span style={styles.weekTitle}>{getWeekLabel()}</span>
          <span style={styles.weekRange}>
            {weekStart.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - {weekEnd.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </span>
        </div>
        <button
          style={styles.navButton}
          onClick={() => setWeekOffset(prev => prev + 1)}
        >
          Next &rarr;
        </button>
        {weekOffset !== 0 && (
          <button
            style={styles.todayButton}
            onClick={() => setWeekOffset(0)}
          >
            Today
          </button>
        )}
      </div>

      {error && <div className="mill-alert mill-alert-error">{error}</div>}

      {loading ? (
        <div className="mill-loading">
          <div className="mill-loading-spinner"></div>
          <p>Loading calendar...</p>
        </div>
      ) : (
        <>
          {/* Week Summary */}
          <div style={styles.weekSummary}>
            <div style={styles.summaryItem}>
              <span style={styles.summaryValue}>{calendarData?.total_orders || 0}</span>
              <span style={styles.summaryLabel}>Total Orders</span>
            </div>
            <div style={styles.summaryItem}>
              <span style={styles.summaryValue}>{(calendarData?.total_weight || 0).toLocaleString()}</span>
              <span style={styles.summaryLabel}>Total lbs</span>
            </div>
          </div>

          {/* Calendar Grid */}
          <div style={styles.calendarGrid}>
            {weekDates.map((date, index) => {
              const orders = getOrdersForDate(date);
              const dayTotals = getDayTotal(date);
              const dateKey = formatDateKey(date);

              return (
                <div
                  key={dateKey}
                  style={{
                    ...styles.dayColumn,
                    ...(isToday(date) ? styles.todayColumn : {}),
                    ...(isPast(date) ? styles.pastColumn : {}),
                  }}
                >
                  {/* Day Header */}
                  <div style={{
                    ...styles.dayHeader,
                    ...(isToday(date) ? styles.todayHeader : {}),
                  }}>
                    <div style={styles.dayName}>
                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </div>
                    <div style={styles.dayDate}>
                      {date.getDate()}
                    </div>
                    {dayTotals.orders > 0 && (
                      <div style={styles.dayTotals}>
                        {dayTotals.orders} orders | {dayTotals.weight.toLocaleString()} lbs
                      </div>
                    )}
                  </div>

                  {/* Day Content */}
                  <div style={styles.dayContent}>
                    {orders.length === 0 ? (
                      <div style={styles.noOrders}>No orders</div>
                    ) : (
                      orders.map((order) => (
                        <div
                          key={order.id}
                          style={{
                            ...styles.orderCard,
                            ...(order.is_rush ? styles.rushOrder : {}),
                          }}
                          onClick={() => navigate(`/mill/dashboard`)}
                        >
                          <div style={styles.orderHeader}>
                            <span style={styles.orderNumber}>#{order.order_number}</span>
                            {order.is_rush && <span style={styles.rushBadge}>RUSH</span>}
                          </div>
                          <div style={styles.orderCustomer}>{order.customer_name}</div>
                          <div style={styles.orderFormula}>
                            {order.formula_name}
                            <span style={{
                              ...styles.formBadge,
                              backgroundColor: order.feed_form === 'PELLET' ? '#e3f2fd' : '#fff3e0',
                              color: order.feed_form === 'PELLET' ? '#1565c0' : '#e65100',
                            }}>
                              {order.feed_form}
                            </span>
                          </div>
                          <div style={styles.orderWeight}>
                            {order.total_quantity_lbs.toLocaleString()} lbs
                            {order.number_of_packages > 0 && (
                              <span style={styles.packageCount}>
                                ({order.number_of_packages} pkg)
                              </span>
                            )}
                          </div>
                          <div style={{
                            ...styles.orderStatus,
                            backgroundColor: order.status === 'PRODUCTION' ? '#e3f2fd' :
                                            order.status === 'READY' ? '#e8f5e9' : '#fff8e1',
                          }}>
                            {order.status_display}
                          </div>
                          <div style={styles.deliveryDate}>
                            Deliver: {new Date(order.delivery_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  weekNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    padding: '1rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    marginBottom: '1rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  navButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
  },
  todayButton: {
    padding: '0.5rem 1rem',
    backgroundColor: '#1a4d1a',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.9rem',
    marginLeft: '0.5rem',
  },
  weekLabel: {
    textAlign: 'center',
    minWidth: '200px',
  },
  weekTitle: {
    display: 'block',
    fontSize: '1.2rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
  },
  weekRange: {
    display: 'block',
    fontSize: '0.85rem',
    color: '#666',
  },
  weekSummary: {
    display: 'flex',
    justifyContent: 'center',
    gap: '2rem',
    padding: '1rem',
    backgroundColor: '#1a4d1a',
    borderRadius: '8px',
    marginBottom: '1rem',
  },
  summaryItem: {
    textAlign: 'center',
  },
  summaryValue: {
    display: 'block',
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#fff',
  },
  summaryLabel: {
    display: 'block',
    fontSize: '0.85rem',
    color: '#81C784',
  },
  calendarGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '0.5rem',
    minHeight: 'calc(100vh - 300px)',
  },
  dayColumn: {
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  todayColumn: {
    boxShadow: '0 0 0 2px #1a4d1a, 0 4px 8px rgba(0,0,0,0.15)',
  },
  pastColumn: {
    opacity: 0.7,
    backgroundColor: '#f9f9f9',
  },
  dayHeader: {
    padding: '0.75rem',
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #e0e0e0',
    textAlign: 'center',
  },
  todayHeader: {
    backgroundColor: '#1a4d1a',
    color: '#fff',
  },
  dayName: {
    fontSize: '0.75rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  dayDate: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    lineHeight: 1.2,
  },
  dayTotals: {
    fontSize: '0.7rem',
    marginTop: '0.25rem',
    opacity: 0.8,
  },
  dayContent: {
    flex: 1,
    padding: '0.5rem',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  noOrders: {
    textAlign: 'center',
    color: '#999',
    padding: '1rem',
    fontSize: '0.85rem',
    fontStyle: 'italic',
  },
  orderCard: {
    padding: '0.5rem',
    backgroundColor: '#f9f9f9',
    borderRadius: '4px',
    borderLeft: '3px solid #4CAF50',
    cursor: 'pointer',
    fontSize: '0.8rem',
  },
  rushOrder: {
    borderLeft: '3px solid #f44336',
    backgroundColor: '#fff5f5',
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.25rem',
  },
  orderNumber: {
    fontWeight: 'bold',
    color: '#1a4d1a',
  },
  rushBadge: {
    backgroundColor: '#f44336',
    color: '#fff',
    padding: '0.1rem 0.3rem',
    borderRadius: '3px',
    fontSize: '0.6rem',
    fontWeight: 'bold',
  },
  orderCustomer: {
    fontWeight: 500,
    color: '#333',
    marginBottom: '0.2rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  orderFormula: {
    color: '#666',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    flexWrap: 'wrap',
  },
  formBadge: {
    padding: '0.1rem 0.25rem',
    borderRadius: '3px',
    fontSize: '0.6rem',
    fontWeight: 500,
  },
  orderWeight: {
    fontWeight: 600,
    color: '#1a4d1a',
    marginTop: '0.25rem',
  },
  packageCount: {
    fontWeight: 400,
    color: '#666',
    fontSize: '0.7rem',
    marginLeft: '0.25rem',
  },
  orderStatus: {
    marginTop: '0.25rem',
    padding: '0.15rem 0.35rem',
    borderRadius: '3px',
    fontSize: '0.65rem',
    fontWeight: 500,
    textAlign: 'center',
  },
  deliveryDate: {
    marginTop: '0.25rem',
    color: '#666',
    fontSize: '0.7rem',
  },
};

export default MillCalendar;
